import {createHook, createStore, StoreActionApi} from 'react-sweet-state';
import {
  getAuthClient,
  IRequestError,
  IRequestUserStatus,
  IUserStatus
} from "./auth";
import axios from "axios";

export interface IUserState extends IUserStatus {
  id: string;

  isAuthenticated: boolean,

  isInitialized: boolean,
  isLoading: boolean,
}

type StoreApi = StoreActionApi<IUserState>;
type Actions = typeof actions;


const initialState: IUserState = {
  id: null,
  mail: null,
  name: null,

  isAuthenticated: false,

  isInitialized: false,
  isLoading: false,
};

let LoadingPromise = null;
let LoadingLoginPromise: Promise<IUserState> = null;

const resetPromises = () => {
  LoadingPromise = null;
  LoadingLoginPromise = null;
};

window.addEventListener('pageshow', (ev) => {
  // fix safari cache on back button #1732
  if (ev.persisted) {
    resetPromises();
  }
});

const processUserStatusResponse = (data: IRequestUserStatus): IUserState => {
  const state = {
    ...initialState,
    ...data.current_user,
    isAuthenticated: data.is_authenticated,
    isInitialized: true
  };
  if (state.isAuthenticated) {
    window.localStorage.setItem('csrfToken', JSON.stringify(data.csrf_token));
    window.localStorage.setItem('logoutToken', JSON.stringify(data.logout_token));
    axios.defaults.withCredentials = true;
    axios.defaults.headers.common['X-CSRF-TOKEN'] = data.csrf_token;
  }
  return state;
}


const actions = {
  getStatus:
    () =>
      ({ setState, getState }: StoreApi) => {
        return new Promise<IUserState>((resolve: (userData: IUserState) => void, reject: (resp: IRequestError) => void) => {
          if (getState().isInitialized) {
            return resolve(getState());
          } else {
            if (!LoadingPromise) {
              LoadingPromise = getAuthClient().retrieveUserStatus()
                .then((data) => {
                  const state = processUserStatusResponse(data);
                  setState(state);
                  LoadingPromise = null;
                  return state;
                })
                .catch((e) => {
                  reject(e);
                  setState({ isLoading: false });
                });
              setState({ isLoading: true });
            }
            return LoadingPromise;
          }
        });
      },

  refreshStatus:
    () =>
      ({ setState, getState, dispatch }: StoreApi) => {
        const state = getState();
        setState({ ...state, isInitialized: false });
        return dispatch(actions.getStatus());
      },

  logout:
    () =>
      ({ setState, getState, dispatch }: StoreApi) => {
        setState({ isLoading: true });
        return getAuthClient().logout().then(() => {
          setState({ ...initialState, isInitialized: true });
        });
      },

  login:
    (name?: string, pass?: string) =>
      ({ setState, getState }: StoreApi) => {
        setState({ isLoading: true });
        if (!LoadingLoginPromise) {
          LoadingLoginPromise = getAuthClient().login(name, pass)
            .then((data) => {
              const state = processUserStatusResponse(data);
              setState(state);
              LoadingPromise = null;
              return state;
            })
            .catch((error) => {
              setState({
                ...initialState,
                isLoading: false,
              });
              LoadingLoginPromise = null;
              return Promise.reject(error);
            });
        }
        return LoadingLoginPromise;
      },

  register:
    (name?: string, pass?: string) =>
      ({ setState, getState }: StoreApi) => {
        setState({ isLoading: true });
        if (!LoadingLoginPromise) {
          LoadingLoginPromise = getAuthClient().register(name, pass)
            .then((data) => {
              const state = processUserStatusResponse(data);
              setState(state);
              LoadingPromise = null;
              return state;
            })
            .catch((error) => {
              setState({
                ...initialState,
                isLoading: false,
              });
              LoadingLoginPromise = null;
              return Promise.reject(error);
            });
        }
        return LoadingLoginPromise;
      },
}


const Store = createStore<IUserState, Actions>({
  initialState,
  actions,
  // optional, mostly used for easy debugging
  name: 'user',
});


const useUser = createHook<IUserState, Actions>(Store);
const useInitUser = createHook(Store, {
  selector: null,
});

export { useUser, useInitUser };
export default useUser;
